/**
 * DistributionAgent.ts
 * 
 * The Digital Distribution Chief - Industrial Direct-to-DSP Engine.
 * Phase 4 "Bank Layer" Agent with Tax Compliance Officer capabilities.
 */

import { AgentConfig } from "../types";
import { DistributionTools } from '../tools/DistributionTools';
import { CatalogAdminTools } from '../tools/CatalogAdminTools';
import { MusicTools } from '../tools/MusicTools';
import { UniversalTools } from '../tools/UniversalTools';
import { FormatFoundryTools } from '../tools/FormatFoundryTools';
import systemPrompt from '@agents/distribution/prompt.md?raw';


const distributionRetrievalConfig = {
    'ddexReleases': { path: 'ddexReleases', requiresUserIdFilter: true },
    'proprietaryIngestionReleases': { path: 'proprietaryIngestionReleases', requiresUserIdFilter: true },
    'distribution_tasks': { path: 'distribution_tasks', requiresUserIdFilter: true },
    'isrc_registry': { path: 'isrc_registry', requiresUserIdFilter: true },
    'upc_registry': { path: 'upc_registry', requiresUserIdFilter: true }
};
const distributionRetrievalTools = buildDomainRetrievalTools('Distribution', distributionRetrievalConfig);
const distributionRetrievalDeclarations = buildDomainRetrievalDeclarations('Distribution', distributionRetrievalConfig);

export const DistributionAgent: AgentConfig = {
    id: "distribution",
    name: "Distribution Director",
    description: "Specializes in high-fidelity industrial distribution and DDEX supply chain management.",
    color: "bg-cyan-500",
    category: "department",
    systemPrompt: systemPrompt,
    get functions() {
        return {
            ...distributionRetrievalTools,
            prepare_release: DistributionTools.prepare_release,
            run_audio_qc: DistributionTools.run_audio_qc,
            issue_isrc: DistributionTools.issue_isrc,
            certify_tax_profile: DistributionTools.certify_tax_profile,
            calculate_payout: DistributionTools.calculate_payout,
            run_metadata_qc: DistributionTools.run_metadata_qc,
            generate_bwarm: DistributionTools.generate_bwarm,
            check_merlin_status: DistributionTools.check_merlin_status,
            check_dsp_delivery_status: DistributionTools.check_dsp_delivery_status,
            validate_metadata: DistributionTools.validate_metadata,
            validate_metadata_readiness: DistributionTools.validate_metadata_readiness,
            create_music_metadata: MusicTools.create_music_metadata,
            verify_metadata_golden: MusicTools.verify_metadata_golden,
            update_track_metadata: MusicTools.update_track_metadata,
            web_extract: UniversalTools.web_extract,
            pro_scraper: UniversalTools.pro_scraper,
            payment_gate: UniversalTools.payment_gate,
            credential_vault: UniversalTools.credential_vault,
            draft_dsp_metadata: McpTools.draft_dsp_metadata,
            catalog_query_gaps: CatalogAdminTools.catalog_query_gaps,
            catalog_stage_registration_payload: CatalogAdminTools.catalog_stage_registration_payload,
            catalog_dispatch_split_invitations: CatalogAdminTools.catalog_dispatch_split_invitations,
            foundry_inspect_format: FormatFoundryTools.foundry_inspect_format,
            foundry_parse_and_validate: FormatFoundryTools.foundry_parse_and_validate,
            foundry_normalize_to_graph: FormatFoundryTools.foundry_normalize_to_graph
        } as Record<string, import('@/services/agent/types').AnyToolFunction>;
    },
    authorizedTools: ['list_domain_records', 'prepare_release', 'run_audio_qc', 'issue_isrc', 'certify_tax_profile', 'calculate_payout', 'run_metadata_qc', 'generate_bwarm', 'check_merlin_status', 'check_dsp_delivery_status', 'validate_metadata', 'validate_metadata_readiness', 'create_music_metadata', 'verify_metadata_golden', 'update_track_metadata', 'web_extract', 'pro_scraper', 'payment_gate', 'credential_vault', 'draft_dsp_metadata', 'catalog_query_gaps', 'catalog_stage_registration_payload', 'catalog_dispatch_split_invitations', 'foundry_inspect_format', 'foundry_parse_and_validate', 'foundry_normalize_to_graph'],
    tools: [{
        functionDeclarations: [
            ...distributionRetrievalDeclarations,
            {
                name: "catalog_query_gaps",
                description: "Query the administrative task queue: audit gaps, staged registration drafts, and outstanding split invitations for the artist's catalog.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        entityType: { type: "STRING", enum: ["master", "track", "release", "composition", "collaborator"] },
                        severity: { type: "STRING", enum: ["info", "warning", "critical", "blocking"] },
                        status: { type: "STRING", enum: ["open", "action_ready", "awaiting_confirmation", "executed", "dismissed", "failed"] },
                        limit: { type: "NUMBER", description: "Max tasks to return (1-100, default 25)" }
                    },
                    required: []
                }
            },
            {
                name: "catalog_stage_registration_payload",
                description: "Build and STAGE a pre-filled registration payload (ISWC/CWR/DDEX_ERN) from a stored catalog record. Draft only — requires human confirmation to execute; nothing is submitted.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        registry: { type: "STRING", enum: ["ISWC", "CWR", "DDEX_ERN"] },
                        releaseId: { type: "STRING", description: "proprietaryIngestionReleases document id" },
                        trackId: { type: "STRING", description: "Track library id (used when no releaseId)" },
                        masterHash: { type: "STRING", description: "Optional master content hash to link" }
                    },
                    required: ["registry"]
                }
            },
            {
                name: "catalog_dispatch_split_invitations",
                description: "Stage split-sheet signature invitations for all collaborators on a release. Idempotent per collaborator+sheet; requires human confirmation before anything is sent.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseId: { type: "STRING" },
                        channel: { type: "STRING", enum: ["in_app", "email"] }
                    },
                    required: ["releaseId"]
                }
            },
            {
                name: "prepare_release",
                description: "Prepare a release for distribution by generating a DDEX ERN 4.3 message.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        title: { type: "STRING", description: "Release title" },
                        artist: { type: "STRING", description: "Primary artist name" },
                        upc: { type: "STRING", description: "UPC barcode (12-13 digits)" },
                        isrc: { type: "STRING", description: "ISRC for the primary track" },
                        label: { type: "STRING", description: "Label name" },
                        genre: { type: "STRING", description: "Primary release genre" },
                        language: { type: "STRING", description: "ISO language code for lyrics or zxx for instrumental" },
                        releaseDate: { type: "STRING", description: "Release date in YYYY-MM-DD format" },
                        releaseType: { type: "STRING", enum: ["Single", "EP", "Album"], description: "Single, EP, or Album" }
                    },
                    required: ["title", "artist", "upc", "isrc", "label", "genre", "language", "releaseDate"]
                }
            },
            {
                name: "draft_dsp_metadata",
                description: "Draft an ERN XML fragment from release metadata via the remote MCP backend.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseTitle: { type: "STRING" },
                        artists: { type: "ARRAY", items: { type: "STRING" } },
                        genre: { type: "STRING" },
                        isrc: { type: "STRING" },
                        upc: { type: "STRING" },
                        duration: { type: "STRING" },
                        releaseDate: { type: "STRING" },
                        mode: { type: "STRING", enum: ["draft", "delivery"] }
                    },
                    required: ["releaseTitle", "artists", "genre", "upc", "isrc"]
                }
            },
            {
                name: "run_audio_qc",
                description: "Run audio quality control to detect fraud and verify Hi-Res/Atmos compliance.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        filePath: { type: "STRING", description: "Path to the audio file" },
                        checkAtmos: { type: "BOOLEAN", description: "Whether to check Dolby Atmos compliance" }
                    },
                    required: ["filePath"]
                }
            },
            {
                name: "issue_isrc",
                description: "Issue a new ISRC (International Standard Recording Code) for a track.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        trackTitle: { type: "STRING", description: "Title of the track" },
                        artist: { type: "STRING", description: "Artist name" },
                        year: { type: "NUMBER", description: "Release year (defaults to current year)" }
                    },
                    required: ["trackTitle", "artist"]
                }
            },
            {
                name: "certify_tax_profile",
                description: "Guide a user through W-8BEN/W-9 tax certification and validate their TIN.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        userId: { type: "STRING", description: "User identifier" },
                        isUsPerson: { type: "BOOLEAN", description: "Is the user a US person?" },
                        isEntity: { type: "BOOLEAN", description: "Is the user an entity (not individual)?" },
                        country: { type: "STRING", description: "Country of residence (ISO code)" },
                        tin: { type: "STRING", description: "Tax Identification Number" },
                        signedUnderPerjury: { type: "BOOLEAN", description: "Has the user signed under penalties of perjury?" }
                    },
                    required: ["userId", "isUsPerson", "country", "tin", "signedUnderPerjury"]
                }
            },
            {
                name: "calculate_payout",
                description: "Calculate royalty distribution using waterfall logic (Fee → Recoup → Splits).",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        grossRevenue: { type: "NUMBER", description: "Total gross revenue to distribute" },
                        indiiFeePercent: { type: "NUMBER", description: "indii platform fee percentage (default: 10)" },
                        recoupableExpenses: { type: "NUMBER", description: "Expenses to recoup before splits" },
                        splits: {
                            type: "ARRAY",
                            description: "Array of payee splits { name, percentage }",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    name: { type: "STRING" },
                                    percentage: { type: "NUMBER" }
                                }
                            }
                        }
                    },
                    required: ["grossRevenue", "splits"]
                }
            },
            {
                name: "run_metadata_qc",
                description: "Run metadata quality control against Apple/Spotify style guides.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        title: { type: "STRING", description: "Track or release title" },
                        artist: { type: "STRING", description: "Artist name" },
                        artworkUrl: { type: "STRING", description: "URL to the cover artwork" }
                    },
                    required: ["title", "artist"]
                }
            },
            {
                name: "generate_bwarm",
                description: "Generate MLC BWARM CSV for mechanical licensing registration with The MLC.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        works: {
                            type: "ARRAY",
                            description: "Array of musical works to register",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    title: { type: "STRING", description: "Work title" },
                                    writer_last: { type: "STRING", description: "Writer last name" },
                                    writer_first: { type: "STRING", description: "Writer first name" },
                                    writer_ipi: { type: "STRING", description: "Writer IPI number (optional)" }
                                }
                            }
                        }
                    },
                    required: ["works"]
                }
            },
            {
                name: "check_merlin_status",
                description: "Check Merlin Network compliance readiness for independent distribution.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        total_tracks: { type: "NUMBER", description: "Total number of tracks in catalog" },
                        has_isrcs: { type: "BOOLEAN", description: "Whether all tracks have ISRCs assigned" },
                        has_upcs: { type: "BOOLEAN", description: "Whether all releases have UPCs assigned" },
                        exclusive_rights: { type: "BOOLEAN", description: "Whether you hold exclusive rights to all content" }
                    },
                    required: ["total_tracks", "has_isrcs", "has_upcs", "exclusive_rights"]
                }
            },
            {
                name: "check_dsp_delivery_status",
                description: "Checks the delivery status of a release to DSPs (Digital Service Providers).",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseId: { type: "STRING", description: "The ID of the release to check" },
                        dspName: { type: "STRING", description: "Optional name of a specific DSP to check (e.g., Spotify, Apple Music)" }
                    },
                    required: ["releaseId"]
                }
            },
            {
                name: "validate_metadata",
                description: "Validate music release metadata for DSP compliance, including ISRC/UPC validation, GTIN check-digits, and formatting style guides.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseId: { type: "STRING", description: "Optional release document ID to validate from Firestore" },
                        title: { type: "STRING", description: "Release title" },
                        artist: { type: "STRING", description: "Primary artist name" },
                        upc: { type: "STRING", description: "UPC / EAN barcode (12-13 digits) to validate" },
                        isrc: { type: "STRING", description: "Primary track ISRC (12 alphanumeric chars)" },
                        artworkUrl: { type: "STRING", description: "Cover artwork URL" },
                        strict: { type: "BOOLEAN", description: "Whether to fail on missing optional fields (default: false)" },
                        tracks: {
                            type: "ARRAY",
                            description: "Array of tracks to validate for ISRC formatting and uniqueness",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    title: { type: "STRING" },
                                    isrc: { type: "STRING" },
                                    track_number: { type: "NUMBER" }
                                }
                            }
                        }
                    }
                }
            },
            {
                name: "validate_metadata_readiness",
                description: "Validates if a release's metadata is ready and complete for distribution.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseId: { type: "STRING", description: "The ID of the release to validate" }
                    },
                    required: ["releaseId"]
                }
            },
            {
                name: "create_music_metadata",
                description: "Highly advanced tool that analyzes audio and creates industry-standard 'Golden Metadata'. This metadata is DDEX-ready and includes AI-detected genre, mood, and identifiers.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        uploadedAudioIndex: { type: "NUMBER", description: "Index of the uploaded audio file in the session gallery." },
                        artistName: { type: "STRING", description: "Name of the artist (optional, will attempt to detect if not provided)." },
                        trackTitle: { type: "STRING", description: "Title of the track (optional, will attempt to detect if not provided)." },
                        releaseType: { type: "STRING", description: "Single, EP, or Album (default: Single)." }
                    },
                    required: ["uploadedAudioIndex"]
                }
            },
            {
                name: "verify_metadata_golden",
                description: "Verifies if a metadata object meets the industrial 'Golden Standard' (valid schema, splits sum to 100%).",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        metadata: { type: "OBJECT", description: "The metadata object to verify." }
                    },
                    required: ["metadata"]
                }
            },
            {
                name: "update_track_metadata",
                description: "Updates specific fields in a track's metadata in the library.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        fingerprint: { type: "STRING", description: "The unique master fingerprint of the track." },
                        updates: { type: "OBJECT", description: "The fields to update." }
                    },
                    required: ["fingerprint", "updates"]
                }
            },
            {
                name: "web_extract",
                description: "Read bounded text from a public web page. This tool cannot log in, click, type, or submit forms.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        url: { type: "STRING", description: "Public HTTP(S) URL to read." }
                    },
                    required: ["url"]
                }
            },
            {
                name: "pro_scraper",
                description: "Scrape PRO repertories (ASCAP/BMI) for Chain of Title audits.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        query: { type: "STRING", description: "Search query (Artist or Song Title)" },
                        society: { type: "STRING", enum: ["ASCAP", "BMI"], description: "Society to search: ASCAP or BMI" }
                    },
                    required: ["query", "society"]
                }
            },
            {
                name: "payment_gate",
                description: "Pause automation to request user approval for a fee.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        amount: { type: "NUMBER", description: "Amount to charge" },
                        vendor: { type: "STRING", description: "Vendor name (e.g. US Copyright Office)" },
                        reason: { type: "STRING", description: "Reason for the charge" }
                    },
                    required: ["amount", "vendor", "reason"]
                }
            },
            {
                name: "credential_vault",
                description: "Securely retrieve stored credentials for external services.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        action: { type: "STRING", enum: ["retrieve", "store"], description: "retrieve or store" },
                        service: { type: "STRING", description: "Service identifier (e.g. ASCAP)" },
                        bio_token: { type: "STRING", description: "Biometric session token" }
                    },
                    required: ["action", "service"]
                }
            },
            {
                name: "foundry_inspect_format",
                description: "Inspect unknown distributor statements, DDEX releases, or royalty exports using Format Forensics Engine.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        evidenceId: { type: "STRING", description: "Identifier for the file or evidence." },
                        content: { type: "STRING", description: "Raw statement or metadata file content (CSV/JSON/XML/TSV)." }
                    },
                    required: ["evidenceId", "content"]
                }
            },
            {
                name: "foundry_parse_and_validate",
                description: "Deterministically parse distributor statements and run 7-stage layered validation against industry standards.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        content: { type: "STRING", description: "Raw statement content to parse and validate." },
                        formatId: { type: "STRING", description: "Optional known format identifier (e.g. 'distrokid-tsv', 'tunecore-csv')." }
                    },
                    required: ["content"]
                }
            },
            {
                name: "foundry_normalize_to_graph",
                description: "Normalize parsed distributor transactions and metadata into the canonical Artist Business Graph (ABG).",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        content: { type: "STRING", description: "Raw file content to normalize." },
                        catalogMap: { type: "OBJECT", description: "Map of track titles/ISRCs to ExtendedGoldenMetadata." }
                    },
                    required: ["content", "catalogMap"]
                }
            }
        ]
    }]
};

import { freezeAgentConfig } from '../FreezeDiagnostic';
import { buildDomainRetrievalTools, buildDomainRetrievalDeclarations } from '../tools/DomainTools';
import { McpTools } from '../tools/McpTools';


// Freeze the schema to prevent cross-test contamination
freezeAgentConfig(DistributionAgent);
